// Copyright (c) Ping. All rights reserved.

import React from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
  useColorScheme,
} from 'react-native';
import { createTheme } from '../design-system/theme';
import { profileStrings } from './strings';
import type { MiniProfileActionKey, UserProfile } from './types';

export interface MiniProfileActionsProps {
  readonly profile: UserProfile;
  readonly onAction: (action: MiniProfileActionKey) => void;
}

const ACTION_KEYS: readonly MiniProfileActionKey[] = ['message', 'call', 'viewProfile', 'more'];

/**
 * Action row at the bottom of the mini-profile card. Disables `message` and
 * `call` if the profile says so (blocked, contact-only, etc.) but always
 * surfaces `viewProfile` and `more` so the host can promote into the full
 * profile or open additional options.
 */
export const MiniProfileActions = React.memo<MiniProfileActionsProps>(({ profile, onAction }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const isDisabled = (key: MiniProfileActionKey): boolean => {
    if (key === 'message') {
      return !profile.canMessage;
    }
    if (key === 'call') {
      return !profile.canCall;
    }
    return false;
  };

  return (
    <View style={[styles.container, { borderTopColor: theme.colors.border.secondary }]}>
      {ACTION_KEYS.map((key) => {
        const disabled = isDisabled(key);
        const isPrimary = key === 'message';
        return (
          <Pressable
            key={key}
            onPress={() => {
              if (!disabled) {
                onAction(key);
              }
            }}
            accessibilityRole="button"
            accessibilityLabel={profileStrings.action(key)}
            accessibilityState={{ disabled }}
            disabled={disabled}
            style={({ pressed }) => [
              styles.button,
              isPrimary && {
                backgroundColor: disabled
                  ? theme.colors.surface.tertiary
                  : theme.colors.accent.indigo,
              },
              !isPrimary && {
                backgroundColor: theme.colors.surface.secondary,
              },
              pressed && !disabled && { opacity: 0.85 },
            ]}
          >
            <Text
              style={[
                styles.buttonText,
                {
                  color:
                    isPrimary && !disabled
                      ? '#FFFFFF'
                      : disabled
                        ? theme.colors.text.tertiary
                        : theme.colors.text.primary,
                },
              ]}
            >
              {profileStrings.action(key)}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
});

MiniProfileActions.displayName = 'MiniProfileActions';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  button: {
    flexGrow: 1,
    flexBasis: '45%',
    margin: 4,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 32,
  } satisfies ViewStyle,
  buttonText: {
    fontSize: 13,
    fontWeight: '600',
  } satisfies TextStyle,
});
